// Shared copy semantics for bringing existing questions into the editor —
// used by ImportQuestionsModal (manual picking) and GenerateFromChaptersModal
// (random pulls). Copies carry chapter identity + provenance, never ids.

// kind='difficulty' shows the label itself (Advanced / Intermediate) — the
// number is just the stable identity behind it.
export const chapterLabel = (ch) => {
    if (!ch) return null;
    if (ch.kind === 'difficulty') return ch.label || `Level ${ch.number}`;
    return `${ch.kind === 'lesson' ? 'Lesson' : 'Ch'} ${ch.number}`;
};

// The word for a course's second-level pick — drives dropdown labels.
export const kindWord = (kind) => (kind === 'lesson' ? 'Lesson' : kind === 'difficulty' ? 'Difficulty' : 'Chapter');

// Visual grouping for the Course pickers (Autocomplete groupBy) — subject
// inferred from the course name, with each subject's chapter type in
// parentheses; purely presentational, nothing stored.
const COURSE_GROUP_ORDER = [
    'Math (Chapters)', 'Science (Chapters)', 'English (Difficulty)',
    'Vocab (Lessons)', 'History (Chapters)', 'Other (Chapters)',
];
export const courseGroup = (course) => {
    if (/^Vocab/i.test(course)) return 'Vocab (Lessons)';
    if (/En Vol|^English$/i.test(course)) return 'English (Difficulty)';
    if (/history/i.test(course)) return 'History (Chapters)';
    if (/biolog|chem|science|physic/i.test(course)) return 'Science (Chapters)';
    if (/algebra|geometry|calc|trig|math/i.test(course)) return 'Math (Chapters)';
    return 'Other (Chapters)';
};
// groupBy needs options pre-sorted by group (else MUI repeats headers)
export const sortCoursesForPicker = (courses) => [...courses].sort((a, b) =>
    COURSE_GROUP_ORDER.indexOf(courseGroup(a)) - COURSE_GROUP_ORDER.indexOf(courseGroup(b))
    || a.localeCompare(b));

// Keep passage-mates ADJACENT: random generation (and scattered clicking) can
// interleave questions that share a stimulus group, which makes the passage
// render repeatedly and its "Questions N–M" label span unrelated questions.
// Stable re-order: when a group's first member appears, pull the rest of its
// members up right behind it (relative order preserved everywhere).
export const clusterByGroup = (payloads) => {
    const emitted = new Set();
    const out = [];
    payloads.forEach((p, i) => {
        if (emitted.has(i)) return;
        out.push(p);
        emitted.add(i);
        const gid = p._source_group?.id;
        if (gid == null) return;
        payloads.forEach((p2, j) => {
            if (!emitted.has(j) && p2._source_group?.id === gid) {
                out.push(p2);
                emitted.add(j);
            }
        });
    });
    return out;
};

// Duplicate-detection fingerprint: normalized prompt text. Returns null when
// there's too little text to judge (image-only questions must never collide).
export const contentKey = (q) => {
    const s = String(q.question_prompt || '')
        .replace(/\{#image[^}]*\}/g, '')
        .replace(/<[^>]+>/g, '')
        .toLowerCase()
        .replace(/\s+/g, '');
    return s.length >= 20 ? s : null;
};

export const stripPreview = (text, max = 90) => {
    const s = String(text || '')
        .replace(/\{#image[^}]*\}/g, '[img]')
        .replace(/<[^>]+>/g, '')
        .replace(/\\\(|\\\)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    return s.length > max ? s.slice(0, max) + '…' : s;
};

// Parts arrive either as a tree (getTestById: roots with `subparts`) or flat
// with parent_id (chapter endpoint). Normalize to a tree first.
export const toPartTree = (parts) => {
    if (!parts || parts.length === 0) return [];
    const isFlat = parts.some((p) => p.parent_id != null);
    if (!isFlat) return parts;
    const map = {};
    const roots = [];
    parts.forEach((p) => { map[p.id] = { ...p, subparts: [] }; });
    parts.forEach((p) => {
        if (p.parent_id && map[p.parent_id]) map[p.parent_id].subparts.push(map[p.id]);
        else roots.push(map[p.id]);
    });
    return roots;
};

// Editor-state part shape, WITHOUT ids — these are copies, not references.
export const copyParts = (parts) => (parts || []).map((p) => ({
    part_label: p.part_label,
    part_order: p.part_order,
    criteria_name: p.criteria_name || '',
    max_points: p.max_points,
    rubric_text: p.rubric_text || '',
    rubric_template_id: p.rubric_template_id || '',
    subparts: copyParts(p.subparts || []),
}));

// Normalize an API question (from /tests/:id or /chapters/:id/questions) into
// the editor's question state shape, ready to append to a section. `group` is
// carried separately so the caller can recreate one stimulus group per source
// group. `_`-prefixed keys are consumed by the insert logic and stripped
// before save.
export const toImportPayload = (q, sourceTest, group) => ({
    question_type: q.question_type === 'sa' ? 'multi_answer' : q.question_type,
    question_prompt: q.question_prompt || '',
    answer: q.answer || '',
    max_points: q.max_points || 1,
    accepted_answers: q.accepted_answers ? JSON.stringify(q.accepted_answers) : '',
    choice_group: '', // choice groups don't carry across tests
    chapter_number: q.v2_chapter ? q.v2_chapter.number : '',
    chapter_id: q.chapter_id || null,
    _loaded_chapter_number: q.v2_chapter ? q.v2_chapter.number : '',
    _chapter_course: q.v2_chapter ? q.v2_chapter.course : '',
    _chapter_kind: q.v2_chapter ? q.v2_chapter.kind : '',
    _chapter_label: q.v2_chapter ? q.v2_chapter.label || '' : '',
    metadata: { ...(q.metadata || {}), imported_from: { test_id: sourceTest?.id || null, question_id: q.id } },
    options: (q.v2_question_options || []).map((o, k) => ({
        option_label: o.option_label, option_text: o.option_text || '', order: o.order ?? k,
    })),
    parts: copyParts(toPartTree(q.v2_question_parts)),
    _source_group: group ? {
        id: group.id, group_label: group.group_label || null,
        stimulus_text: group.stimulus_text || null, stimulus_url: group.stimulus_url || null,
    } : null,
});
